import { useState } from 'react';

import {
  PartActionSelection,
  PartActionsStep,
  SelectedPartType,
  SelectedSerial,
} from './MobilePartActionsDialog.props';
import {
  BulkPartActionItem,
  BulkPartActionResponse,
  IN_SERIAL_ACTIONS,
  OUT_SERIAL_ACTIONS,
  buildBulkItem,
  isSecondaryAction,
} from './partActionBuildHelpers';
import { useExceptionalPartSubmit } from './useExceptionalPartSubmit';
import { usePartActionSearch } from './usePartActionSearch';
import { PartActionTypes } from '../../../../../../@types/partAction.type';
import { InteractionFormData, ServiceObject } from '../../../../../../@types/service.type';
import { useAddBulkPartActions } from '../../../../../../queries/partAction-query';
import {
  buildInteractionForms,
  getBlockMessage,
  hasBlockInteraction,
  validateRequiredFields,
} from '../../../../../../utils/mobile-interactions';

export const useMobilePartActions = (
  service: ServiceObject,
  onClose: () => void,
  onPartActionComplete: () => void | Promise<void>
) => {
  const [step, setStep] = useState<PartActionsStep>('select-action');
  const [selectedAction, setSelectedAction] = useState<PartActionSelection | null>(null);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [interactions, setInteractions] = useState<InteractionFormData[]>([]);
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const [originalPayload, setOriginalPayload] = useState<BulkPartActionItem | null>(null);
  const [isBlocked, setIsBlocked] = useState(false);

  const addBulkPartActionsMutation = useAddBulkPartActions(service.id);
  const search = usePartActionSearch(service, selectedAction);

  const executeSubmit = async (
    inSerial: SelectedSerial | null,
    outSerial: SelectedSerial | null,
    isExceptional: boolean
  ): Promise<void> => {
    if (!selectedAction) return;
    setStep('loading-interactions');
    setError(null);
    const bulkItem = buildBulkItem({
      selectedAction,
      serviceId: service.id,
      notes,
      isExceptional,
      inSerial,
      outSerial,
      selectedPartType: search.selectedPartType,
      buildPartTypePartData: search.buildPartTypePartData,
      buildBulkPartData: search.buildBulkPartData,
    });
    try {
      const response = (await addBulkPartActionsMutation.mutateAsync([
        bulkItem,
      ])) as BulkPartActionResponse;
      const interactionData = response?.interactionFormData || [];
      if (interactionData.length > 0) {
        setInteractions(interactionData);
        setOriginalPayload(bulkItem);
        setIsBlocked(hasBlockInteraction(interactionData));
        setStep('interactions');
      } else {
        await onPartActionComplete();
        handleClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add part action');
      setStep('confirm');
    }
  };

  const exceptional = useExceptionalPartSubmit({
    service,
    selectedAction,
    makeSerial: search.makeSerial,
    setInSerial: search.setInSerial,
    setOutSerial: search.setOutSerial,
    getOutSerial: () => search.outSerial,
    getInSerial: () => search.inSerial,
    setError: (msg) => setError(msg || null),
    setStep,
    executeSubmit,
  });

  const handleActionSelect = (action: PartActionTypes): void => {
    setSelectedAction({ code: action.code, name: action.name });
    setError(null);
    if (isSecondaryAction(action.code)) {
      search.loadPartTypeDefaults(action.code);
      setStep('select-part-type');
    } else {
      setStep('confirm');
    }
  };

  const handlePartTypeSelect = (partType: SelectedPartType): void => {
    search.setSelectedPartType(partType);
    setStep('confirm');
  };

  const handleSubmit = async (): Promise<void> => {
    if (!selectedAction) return;
    if (isSecondaryAction(selectedAction.code)) {
      if (!search.selectedPartType) {
        setError('Please select a part type');
        return;
      }
      await executeSubmit(null, null, false);
      return;
    }
    if (OUT_SERIAL_ACTIONS.includes(selectedAction.code) && !search.outSerial) {
      setError('Please select a serial number for the part being removed');
      return;
    }
    if (IN_SERIAL_ACTIONS.includes(selectedAction.code) && !search.inSerial) {
      setError('Please select a serial number for the part being installed');
      return;
    }
    await executeSubmit(
      search.inSerial,
      search.outSerial,
      exceptional.isInSerialExceptional || exceptional.isOutSerialExceptional
    );
  };

  const handleInteractionSubmit = async (): Promise<void> => {
    if (isBlocked) {
      setError(getBlockMessage(interactions));
      return;
    }
    const missing = validateRequiredFields(interactions, formValues);
    if (missing) {
      setError(`Please fill in required field: ${missing}`);
      return;
    }
    setStep('submitting');
    setError(null);
    try {
      const interactionForms = buildInteractionForms(interactions, formValues);
      await addBulkPartActionsMutation.mutateAsync({
        partActionForms: [{ ...originalPayload, execute: true }],
        interactionForms,
        actionTriggers: [],
      });
      await onPartActionComplete();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit part action');
      setStep('interactions');
    }
  };

  const resetState = (): void => {
    setStep('select-action');
    setSelectedAction(null);
    setNotes('');
    setError(null);
    setInteractions([]);
    setFormValues({});
    setOriginalPayload(null);
    setIsBlocked(false);
    exceptional.resetExceptional();
    search.reset();
  };

  const handleClose = (): void => {
    resetState();
    onClose();
  };

  const handleFieldChange = (interactionId: number, fieldId: number, value: unknown): void =>
    setFormValues((prev) => ({ ...prev, [`${interactionId}-${fieldId}`]: value }));
  const handleBlockBypass = (): void => setIsBlocked(false);

  const canSubmit = (): boolean => {
    if (!selectedAction) return false;
    if (isSecondaryAction(selectedAction.code)) return !!search.selectedPartType;
    if (OUT_SERIAL_ACTIONS.includes(selectedAction.code) && !search.outSerial) return false;
    if (IN_SERIAL_ACTIONS.includes(selectedAction.code) && !search.inSerial) return false;
    return true;
  };

  const getTitle = (): string => {
    if (step === 'select-action') return 'Part Actions';
    if (step === 'select-part-type') return 'Select Part Type';
    if (step === 'confirm') return 'Confirm Part Action';
    if (step === 'exceptional-create') return 'Create Part Exceptionally';
    if (step === 'loading-interactions') return 'Checking Requirements...';
    if (step === 'interactions') return 'Complete Required Forms';
    return 'Submitting...';
  };

  return {
    step,
    setStep,
    selectedAction,
    handleActionSelect,
    notes,
    setNotes,
    error,
    interactions,
    formValues,
    isBlocked,
    exceptionalFor: exceptional.exceptionalFor,
    setExceptionalFor: exceptional.setExceptionalFor,
    exceptionalInitialSerial: exceptional.exceptionalInitialSerial,
    canSubmitExceptional: exceptional.canSubmitExceptional,
    setCanSubmitExceptional: exceptional.setCanSubmitExceptional,
    search,
    addBulkPartActionsMutation,
    createExceptionalPartMutation: exceptional.createExceptionalPartMutation,
    handleSubmit,
    handlePartTypeSelect,
    handleInteractionSubmit,
    handleExceptionalSubmit: exceptional.handleExceptionalSubmit,
    handleFieldChange,
    handleBlockBypass,
    resetState,
    handleClose,
    canSubmit,
    getTitle,
    onCreateOutExceptionally: exceptional.onCreateOutExceptionally,
    onCreateInExceptionally: exceptional.onCreateInExceptionally,
  };
};
